import { useContext, useEffect, useState } from 'react';

import { AuthContext } from './auth-context';
import { baseUrl } from './base-url';
import { getOrCacheFile, hasCachedFile, getCachedFile } from './file-cache';

export const buildFileCacheKey = (filename: string, userId?: string) =>
  `${userId ?? 'public'}/${filename}`;

export const isDirectFileUrl = (filename: string) =>
  filename.startsWith('/') ||
  filename.startsWith('http://') ||
  filename.startsWith('https://');

export const buildFileUrlEndpoint = (
  filename: string,
  orgId: string,
  userId?: string
) => {
  const encodedFilename = encodeURIComponent(filename);
  return userId
    ? `${baseUrl}/org/${orgId}/user/${userId}/getUserFileUrl/${encodedFilename}`
    : `${baseUrl}/org/${orgId}/getPublicFileUrl/${encodedFilename}`;
};

export const buildFileUploadEndpoint = (
  filename: string,
  orgId: string,
  userId?: string
) => {
  const encodedFilename = encodeURIComponent(filename);
  return userId
    ? `${baseUrl}/org/${orgId}/user/${userId}/getUserFileUpload/${encodedFilename}`
    : `${baseUrl}/org/${orgId}/getPublicFileUpload/${encodedFilename}`;
};

export const uploadFileToSignedUrl = async (
  file: File,
  orgId: string,
  jwtToken: string,
  userId?: string
) => {
  const uploadResponse = await fetch(
    buildFileUploadEndpoint(file.name, orgId, userId),
    {
      headers: {
        authorization: jwtToken
      },
      method: 'get'
    }
  );

  if (!uploadResponse.ok) {
    throw new Error('Unable to fetch upload URL');
  }

  const { url } = await uploadResponse.json();

  if (!url) {
    throw new Error('Upload URL missing');
  }

  const putResponse = await fetch(url, {
    method: 'put',
    body: file
  });

  if (!putResponse.ok) {
    throw new Error('Unable to upload file');
  }

  return url;
};

export const getFileAccessUrls = async (
  filename: string,
  orgId: string,
  jwtToken: string,
  userId?: string
) => {
  const cacheKey = buildFileCacheKey(filename, userId);
  const downloadResponse = await fetch(
    buildFileUrlEndpoint(filename, orgId, userId),
    {
      headers: {
        authorization: jwtToken
      },
      method: 'get'
    }
  );

  if (!downloadResponse.ok) {
    throw new Error('Unable to fetch file URL');
  }

  const { url: accessibleUrl } = await downloadResponse.json();
  const cacheUrl = await getOrCacheFile(cacheKey, accessibleUrl);
  return { accessibleUrl, cacheUrl };
};

export const useFile = (filename: string, orgId: string, userId?: string) => {
  const cacheKey = buildFileCacheKey(filename, userId);

  const { getToken } = useContext(AuthContext);
  const [fileUrl, setFileUrl] = useState(
    hasCachedFile(cacheKey) ? getCachedFile(cacheKey) : ''
  );
  const [originalUrl, setOriginalUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (!filename) {
      setFileUrl('');
      setOriginalUrl('');
      setLoading(false);
      setError('');
      return;
    }

    if (isDirectFileUrl(filename)) {
      setFileUrl(filename);
      setOriginalUrl(filename);
      setLoading(false);
      setError('');
      return;
    }

    if (hasCachedFile(cacheKey)) {
      setFileUrl(getCachedFile(cacheKey));
      setOriginalUrl('');
      setLoading(false);
      setError('');
      return;
    }

    setFileUrl('');
    setOriginalUrl('');
    setLoading(true);
    setError('');

    const fetchFileUrl = async () => {
      try {
        const { accessibleUrl, cacheUrl } = await getFileAccessUrls(
          filename,
          orgId,
          getToken().getJwtToken(),
          userId
        );

        if (cancelled) {
          return;
        }

        setOriginalUrl(accessibleUrl);
        setFileUrl(cacheUrl);
        setError('');
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Unable to load file', err);
        setOriginalUrl('');
        setFileUrl('');
        setError('Unable to load file');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchFileUrl();
    return () => {
      cancelled = true;
    };
  }, [cacheKey, filename, getToken, orgId, userId]);

  return { url: fileUrl, loading, originalUrl, error };
};
